import {
  Dispatch,
  Fragment,
  RefObject,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Icon } from "~/components/icon";
import { cn } from "~/utils";

import {
  appendChangeComments,
  shouldSkipAttributionForEdit,
  type TextSelection,
} from "./attribution";
import {
  getSettingsOverrideFiles,
  highlightYamlLine,
  type SettingsOverrides,
} from "./helpers";

interface SettingsOverridesEditorProps {
  originalOverrides: SettingsOverrides;
  draftOverrides: SettingsOverrides;
  setDraftOverrides: Dispatch<SetStateAction<SettingsOverrides>>;
  selectedFile: string | null;
  setSelectedFile: Dispatch<SetStateAction<string | null>>;
  newFileName: string;
  setNewFileName: Dispatch<SetStateAction<string>>;
  addFileError: string | null;
  setAddFileError: Dispatch<SetStateAction<string | null>>;
  canCreateSetConfig: boolean;
  validationErrors: Map<string, string>;
  attributionUsername: string;
}

export function SettingsOverridesEditor({
  originalOverrides,
  draftOverrides,
  setDraftOverrides,
  selectedFile,
  setSelectedFile,
  newFileName,
  setNewFileName,
  addFileError,
  setAddFileError,
  canCreateSetConfig,
  validationErrors,
  attributionUsername,
}: SettingsOverridesEditorProps) {
  const [isAddingFile, setIsAddingFile] = useState(false);
  const newFileInputRef = useRef<HTMLInputElement>(null);
  const fileNames = useMemo(
    () => getSettingsOverrideFiles(draftOverrides),
    [draftOverrides],
  );
  const changedFileNames = useMemo(
    () =>
      new Set(
        fileNames.filter(
          (fileName) =>
            originalOverrides[fileName] !== draftOverrides[fileName],
        ),
      ),
    [draftOverrides, fileNames, originalOverrides],
  );

  useEffect(() => {
    if (fileNames.length === 0) {
      setSelectedFile(null);
      return;
    }

    if (!selectedFile || !fileNames.includes(selectedFile)) {
      setSelectedFile(fileNames[0]);
    }
  }, [fileNames, selectedFile, setSelectedFile]);

  useEffect(() => {
    if (isAddingFile) {
      newFileInputRef.current?.focus();
    }
  }, [isAddingFile]);

  const activeFile = selectedFile ?? fileNames[0] ?? null;

  const addFile = () => {
    const fileName = newFileName.trim();

    if (!fileName) {
      setAddFileError("Enter a file name.");
      return;
    }

    if (draftOverrides[fileName] != null) {
      setAddFileError(`${fileName} already exists.`);
      return;
    }

    setDraftOverrides((prev) => ({ ...prev, [fileName]: "" }));
    setSelectedFile(fileName);
    setNewFileName("");
    setAddFileError(null);
    setIsAddingFile(false);
  };

  const removeFile = (fileName: string) => {
    setDraftOverrides((prev) => {
      const next = { ...prev };
      delete next[fileName];
      return next;
    });
  };

  return (
    <div className="space-y-4">
      {addFileError ? (
        <div className="text-sm text-red-600">{addFileError}</div>
      ) : null}

      {!canCreateSetConfig ? (
        <div className="rounded-md bg-orange-50 p-3 text-sm text-orange-700">
          set_config is not available for this cluster right now.
        </div>
      ) : null}

      <div className="space-y-3">
        <EditableFileTabs
          fileNames={fileNames}
          activeFile={activeFile}
          changedFileNames={changedFileNames}
          validationErrors={validationErrors}
          isAddingFile={isAddingFile}
          newFileName={newFileName}
          newFileInputRef={newFileInputRef}
          setSelectedFile={setSelectedFile}
          setNewFileName={setNewFileName}
          setAddFileError={setAddFileError}
          setIsAddingFile={setIsAddingFile}
          addFile={addFile}
          removeFile={removeFile}
        />

        <SettingsOverrideDraftEditor
          fileNames={fileNames}
          activeFile={activeFile}
          draftOverrides={draftOverrides}
          setDraftOverrides={setDraftOverrides}
          attributionUsername={attributionUsername}
        />

        {activeFile && validationErrors.has(activeFile) ? (
          <div role="alert" className="text-sm text-red-600">
            Invalid YAML in {activeFile}: {validationErrors.get(activeFile)}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SettingsOverrideDraftEditor({
  fileNames,
  activeFile,
  draftOverrides,
  setDraftOverrides,
  attributionUsername,
}: {
  fileNames: string[];
  activeFile: string | null;
  draftOverrides: SettingsOverrides;
  setDraftOverrides: Dispatch<SetStateAction<SettingsOverrides>>;
  attributionUsername: string;
}) {
  if (fileNames.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-gray-300 p-4 text-sm text-gray-500">
        No files in this draft. Add a file to configure overrides.
      </div>
    );
  }

  if (!activeFile) {
    return null;
  }

  return (
    <YamlEditor
      value={draftOverrides[activeFile] ?? ""}
      onChange={(value, selection, skipAttribution) => {
        const previousValue = draftOverrides[activeFile] ?? "";
        const annotatedChange = skipAttribution
          ? { value, selection }
          : appendChangeComments(
              previousValue,
              value,
              selection,
              attributionUsername,
            );

        setDraftOverrides((prev) => ({
          ...prev,
          [activeFile]: annotatedChange.value,
        }));

        return annotatedChange.selection;
      }}
    />
  );
}

// Shared by the highlight layer and the textarea.
const YAML_EDITOR_LAYER_CLASS =
  "m-0 block w-full border-0 bg-transparent p-3 font-mono text-xs leading-5 [overflow-wrap:break-word] [tab-size:2] [white-space:pre-wrap] [word-break:keep-all]";

function YamlEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (
    value: string,
    selection: TextSelection,
    skipAttribution: boolean,
  ) => TextSelection;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingSelectionRef = useRef<TextSelection | null>(null);
  const lines = value.length === 0 ? [""] : value.split("\n");

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (
      !pendingSelectionRef.current ||
      !textarea ||
      document.activeElement !== textarea
    ) {
      return;
    }

    const { start, end } = pendingSelectionRef.current;
    textarea.setSelectionRange(start, end);
    pendingSelectionRef.current = null;
  }, [value]);

  return (
    // Scroll the outer box so the highlight + textarea move together
    <div className="h-[24rem] resize-y overflow-auto rounded-md border border-gray-300 bg-slate-950 focus-within:border-blue-500">
      <div className="relative min-h-full overflow-hidden text-slate-200">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(event) => {
            const nextValue = event.target.value;
            pendingSelectionRef.current = onChange(
              nextValue,
              {
                start: event.target.selectionStart,
                end: event.target.selectionEnd,
              },
              shouldSkipAttributionForEdit(value, nextValue),
            );
          }}
          spellCheck={false}
          autoCapitalize="off"
          autoComplete="off"
          autoCorrect="off"
          className={cn(
            YAML_EDITOR_LAYER_CLASS,
            "absolute inset-0 z-10 h-full resize-none overflow-hidden text-transparent caret-white [-webkit-text-fill-color:transparent] selection:bg-blue-500/40 focus:outline-none",
          )}
        />
        <pre
          aria-hidden="true"
          className={cn(
            YAML_EDITOR_LAYER_CLASS,
            "pointer-events-none relative min-h-full",
          )}
        >
          {lines.map((line, index) => (
            <Fragment key={index}>
              {highlightYamlLine(line, {
                reasonPlaceholderStyle: "underline",
              })}
              {index < lines.length - 1 ? "\n" : null}
            </Fragment>
          ))}
          <br />
        </pre>
      </div>
    </div>
  );
}

function EditableFileTabs({
  fileNames,
  activeFile,
  changedFileNames,
  validationErrors,
  isAddingFile,
  newFileName,
  newFileInputRef,
  setSelectedFile,
  setNewFileName,
  setAddFileError,
  setIsAddingFile,
  addFile,
  removeFile,
}: {
  fileNames: string[];
  activeFile: string | null;
  changedFileNames: Set<string>;
  validationErrors: Map<string, string>;
  isAddingFile: boolean;
  newFileName: string;
  newFileInputRef: RefObject<HTMLInputElement>;
  setSelectedFile: Dispatch<SetStateAction<string | null>>;
  setNewFileName: Dispatch<SetStateAction<string>>;
  setAddFileError: Dispatch<SetStateAction<string | null>>;
  setIsAddingFile: Dispatch<SetStateAction<boolean>>;
  addFile: () => void;
  removeFile: (fileName: string) => void;
}) {
  const addFilePlaceholder =
    fileNames.length === 0 ? "default.yaml" : "new-file.yaml";

  return (
    <div
      className="flex gap-1 overflow-x-auto border-b border-gray-200"
      role="tablist"
      aria-label="Editable settings override files"
    >
      {fileNames.map((fileName) => {
        const isSelected = fileName === activeFile;
        const hasChanged = changedFileNames.has(fileName);
        return (
          <EditableFileTab
            key={fileName}
            fileName={fileName}
            isSelected={isSelected}
            hasChanged={hasChanged}
            hasError={validationErrors.has(fileName)}
            onSelect={() => setSelectedFile(fileName)}
            onRemove={() => removeFile(fileName)}
          />
        );
      })}
      {isAddingFile ? (
        <NewFileTab
          newFileName={newFileName}
          newFileInputRef={newFileInputRef}
          placeholder={addFilePlaceholder}
          setNewFileName={setNewFileName}
          setAddFileError={setAddFileError}
          setIsAddingFile={setIsAddingFile}
          addFile={addFile}
        />
      ) : (
        <AddFileTabButton
          onClick={() => {
            setIsAddingFile(true);
            setAddFileError(null);
          }}
        />
      )}
    </div>
  );
}

function EditableFileTab({
  fileName,
  isSelected,
  hasChanged,
  hasError,
  onSelect,
  onRemove,
}: {
  fileName: string;
  isSelected: boolean;
  hasChanged: boolean;
  hasError: boolean;
  onSelect: () => void;
  onRemove: () => void;
}) {
  return (
    <div
      className={cn(
        "flex items-center whitespace-nowrap border-b-2 transition-colors",
        hasError
          ? "bg-red-50 text-red-700"
          : hasChanged
            ? "bg-orange-50 text-orange-700"
            : "text-gray-500 hover:text-gray-700",
        isSelected
          ? "border-blue-500 text-blue-700"
          : "border-transparent hover:border-gray-300",
      )}
    >
      <button
        type="button"
        role="tab"
        aria-selected={isSelected}
        onClick={onSelect}
        className="py-2 pl-3 pr-1 text-xs font-medium"
      >
        {fileName}
        {hasChanged ? (
          <span className="ml-1 text-orange-500" aria-hidden="true">
            *
          </span>
        ) : null}
      </button>
      <button
        type="button"
        onClick={onRemove}
        className="mr-1 rounded p-0.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
        aria-label={`Remove ${fileName}`}
        title={`Remove ${fileName}`}
      >
        <Icon name="Trash2" size={12} />
      </button>
    </div>
  );
}

function NewFileTab({
  newFileName,
  newFileInputRef,
  placeholder,
  setNewFileName,
  setAddFileError,
  setIsAddingFile,
  addFile,
}: {
  newFileName: string;
  newFileInputRef: RefObject<HTMLInputElement>;
  placeholder: string;
  setNewFileName: Dispatch<SetStateAction<string>>;
  setAddFileError: Dispatch<SetStateAction<string | null>>;
  setIsAddingFile: Dispatch<SetStateAction<boolean>>;
  addFile: () => void;
}) {
  return (
    <div className="flex items-center gap-1 border-b-2 border-blue-500 py-1">
      <input
        ref={newFileInputRef}
        type="text"
        value={newFileName}
        onChange={(event) => {
          setNewFileName(event.target.value);
          setAddFileError(null);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            addFile();
          }

          if (event.key === "Escape") {
            event.preventDefault();
            setIsAddingFile(false);
            setNewFileName("");
            setAddFileError(null);
          }
        }}
        placeholder={placeholder}
        className="w-36 rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none"
      />
      <button
        type="button"
        onClick={addFile}
        className="rounded p-1 text-blue-600 hover:bg-blue-50"
        aria-label="Add file"
        title="Add file"
      >
        <Icon name="Check" size={14} />
      </button>
    </div>
  );
}

function AddFileTabButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-px flex items-center justify-center border-b-2 border-transparent px-3 py-2 text-gray-500 hover:border-gray-300 hover:text-blue-600"
      aria-label="Add file"
      title="Add file"
    >
      <Icon name="Plus" size={14} />
    </button>
  );
}
